import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import apiCallFormData from "../../../../utils/apiFormDataCall";
import { NotificationContext } from '../../../../providers/notification';

interface Country {
    CountryId: number;
    CountryName: string;
}
  
interface CountryResponse {
    data: Country[];
}

interface ComponentProps {
    handleClose: () => void;
    data: CountryResponse | undefined;
    isLoading: boolean;
    isError: boolean;
    getCountryErrorMessage: string;
}

const CreateState: React.FC<ComponentProps> = ({ data, isError, isLoading, getCountryErrorMessage, handleClose }) => {
    const { showNotification } = useContext(NotificationContext);
    const queryClient = useQueryClient();

    const postState = (data: FormData) => {
        return apiCallFormData().post('/State/AddState', data);
    };

    const postStateMutation = useMutation(['post-state'], postState, {retry: 0});

    const postStateHandler = async (values: FormikValues, resetForm: () => void) => {
        const formData = new FormData();
        formData.append('CountryId', values?.country);
        formData.append('StateName', values?.state);

        try {
            await postStateMutation.mutateAsync(formData, {
                onSuccess: () => {
                    showNotification({
                        message: 'State successfully added',
                        type: 'success',
                    });

                    queryClient.invalidateQueries(['get-states']);
                    queryClient.invalidateQueries(['get-states-by-country']);

                    resetForm();
                    handleClose();
                }
            })
        } catch (error: any){
            showNotification({
                message: error?.response?.data?.title || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        country: Yup.string().required('required'),
        state: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            country: '',
            state: ''
        },
        onSubmit: (values, {resetForm}) => {
            postStateHandler(values, resetForm);
        },
        validationSchema,
    });

    const countryOptions = data && data?.data.length > 0 ? (
        data?.data.map((country: Country) => (
            <option key={country.CountryId} value={country.CountryId}>
                {country.CountryName}
            </option>
        ))
    ) : (
        <option disabled>
            {isLoading ? "Loading..." : isError ? getCountryErrorMessage : ''}
        </option>
    )

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='country'
                        type='text'
                        as={Select}
                        label='Country'
                        options={countryOptions}
                    />
                    <Field 
                        name='state'
                        type='text'
                        as={Input}
                        label='State'
                        placeholder='State'
                    />
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={postStateMutation.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={postStateMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default CreateState;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

